import React, { CSSProperties, useEffect, useRef, useState } from "react";
import cls from "classnames";
import styles from "./GenericChatSection.module.scss";
import { TextInput, TextInputProps } from "../../core/field/TextInput";
import { Button } from "../../core/Button/Button";
import FontAwesomeIcon from "../../core/fa_icon";
import Placeholder from "../../core/placeholder";
import { formatDate } from "../../../utils/date";
import TaskIndicator from "../../core/task_indicator";
import { SectionTransitionedContainer } from "../../core/SectionTransitionedContainer/SectionTransitionedContainer";
import { TelephonySection } from "../TelephonyViewportWidget/TelephonySection/TelephonySection";
import { TooltipTrigger } from "../../core/Tooltip/TooltipTrigger";
import escapeStringRegexp from "escape-string-regexp";
import { text } from "../../../utils/i18n";
import { useStore } from "react-stores";
import { globalUserStore } from "../../user/UserDispatcher";
import { TransitionedContainer } from "../../core/transitioned_container";
import { isMobile } from "../../../utils/browser";

export type GenericChatMessage = {
  id: string;
  text: string;
  status: {
    busy?: {};
    seen?: Date;
    sent?: Date;
    notSent?: {};
  } | null;
  createdAt: Date;
  tooltip?: string | false;
  dateText?: string;
  error?: any;
  isConfetti?: boolean;
  isThem?: boolean;
  userId?: string;
  props?: any;
}

export type ChatStandardMessage = {
  text: string;
  description?: string;
  sendMessage?: () => void;
  isQueried?: (query: string) => boolean;
}

export enum GenericChatSectionTab {
  History = "1",
  StandardMessages = "2"
}

type Props = {
  id: string;
  ingress?: string;
  history: { isRefreshing: boolean; messages: GenericChatMessage[]; error?: any };
  sendMessage?: (text: string, feeling?: string) => Promise<void>;
  isRefreshingStandardMessages?: boolean;
  isRefreshingHistory?: boolean;
  standardMessages?: ChatStandardMessage[];
  tab: GenericChatSectionTab;
  inputMaxLength?: number;
  inputEnterSendsMessage?: boolean;
  inputProps?: Partial<TextInputProps>;
  style?: CSSProperties;
} & Pick<HistoryListProps, 'isTyping'>;

/**
 * Section for viewport widget. Allows sending and listing historical messages.
 */
export const GenericChatSection: React.FC<Props> = ({
  id,
  ingress,
  history,
  sendMessage,
  isRefreshingHistory,
  isRefreshingStandardMessages,
  tab,
  standardMessages,
  inputMaxLength,
  inputEnterSendsMessage = true,
  inputProps,
  style,
  isTyping,
}) => {

  const [inputText, setInputText] = useState<string>('');
  const [areStandardMessagesDisabled, setStandardMessagesDisabled] = useState(false);

  useEffect(() => {
    if (areStandardMessagesDisabled) {
      const enableId = setTimeout(() => setStandardMessagesDisabled(false), 1000);
      return () => { clearTimeout(enableId); }
    }
  }, [areStandardMessagesDisabled]);

  const standardMessageQuery = inputText;
  let queriedStandardMessages = standardMessages;

  if (standardMessageQuery) {
    const regex = new RegExp(escapeStringRegexp(standardMessageQuery), 'i');
    queriedStandardMessages = queriedStandardMessages?.filter(message =>
      message.isQueried?.(standardMessageQuery) ?? regex.test(message.text));
  }

  function submitInput() {
    const messageText = inputText.trim();
    if (!messageText) {
      return false;
    }

    sendMessage(messageText);

    setInputText('');
  }

  return (
    <TelephonySection className={styles.container} id={id} style={style}>
      <SectionTransitionedContainer
        props={{
          style: { flexGrow: 1, width: '100%' },
          transitionContainerStyle: { height: '100%', width: '100%' }
        }}
        section={tab}>
        {
          tab === GenericChatSectionTab.StandardMessages ? (
            <div style={{ position: 'relative', height: '100%' }}>
              <div className={styles.standardMessageList}>
                {
                  isRefreshingStandardMessages && !standardMessages.length ? (
                    <TaskIndicator type="tiny" center />
                  ) : (
                    <>
                      {
                        queriedStandardMessages.map((message, index) => (
                          <TooltipTrigger style={{ width: '100%' }} tooltip={message.description}>
                            <Button
                              style={{ width: '100%' }}
                              size="compact"
                              key={index}
                              disabled={areStandardMessagesDisabled}
                              onClick={() => {
                                setInputText('');
                                setStandardMessagesDisabled(true);

                                if (typeof message.sendMessage === 'function') {
                                  message.sendMessage();

                                } else {
                                  sendMessage(message.text);
                                }
                              }}>
                              {message.text}
                            </Button>
                          </TooltipTrigger>
                        ))
                      }
                    </>
                  )
                }
              </div>
            </div>
          ) : tab === GenericChatSectionTab.History ? (
            <ChatHistoryList
              id={id}
              ingress={ingress}
              isBusy={isRefreshingHistory}
              messages={history.messages}
              isTyping={isTyping} />
          ) : null
        }
      </SectionTransitionedContainer>

      {
        sendMessage ? (
          <form
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: inputProps?.inputStyle === 1 ? 'center' : 'flex-end',
              gap: 8,
              marginTop: 8
            }}
            className={styles.inputContainer}
            onSubmit={event => {
              event.preventDefault();
              submitInput();
              return false;
            }}>
            <TextInput
              placeholder={tab === GenericChatSectionTab.StandardMessages && standardMessages.length ? text`Ange eller sök text` : text`Ange text`}
              value={inputText}
              onTextValueChange={text => setInputText(text)}
              maxLength={inputMaxLength}
              isLengthCounterEnabled
              multiline
              maxRows={3}
              enterKeyHint={inputEnterSendsMessage ? "send" : "enter"}
              {...inputProps ?? {}}
              onFocus={event => {
                event.currentTarget.scrollIntoView({ behavior: "smooth" });
              }}
              onKeyPress={event => {
                //  Shift + enter for multline. Enter to send.
                let sendNow = false;
                if (inputEnterSendsMessage) {
                  sendNow = event.key === "Enter";
                
                } else {
                  sendNow = event.key === "Enter" && !event.shiftKey && !isMobile;
                }

                if (sendNow) {
                  submitInput();
                  event.preventDefault();
                }

                return inputProps?.onKeyPress(event);
              }}
            />
            <Button
              icon="paper-plane"
              disabled={!inputText.trim()}
              weight="primary"
            />
          </form>
        ) : null
      }
    </TelephonySection >
  )
}

type HistoryListProps = {
  /**
   * Used for preserving scroll between transitions.
   */
  id: string;

  ingress?: string;
  isBusy?: boolean;
  isTyping?: boolean | Date | null;
  messages: GenericChatMessage[];
  /**
   * @default true
   */
  autoScrollToEnd?: boolean;
  props?: any;
}

const historyListScrollTop: ById<number> = {};

/**
 * Name of the attribute on list items which makes it focused.
 */
export const chatHistoryFocusedAttribute = 'data-focused';

export const ChatHistoryList: React.FC<HistoryListProps> = ({ id, ingress, isBusy, messages, autoScrollToEnd, props, isTyping }) => {
  autoScrollToEnd = autoScrollToEnd ?? true;

  const listRef = useRef<HTMLDivElement>(null);
  const previousMessageLength = useRef(messages.length);

  const [nonce, setNonce] = useState(0);

  if (isTyping instanceof Date) {
    isTyping = isTyping.getTime() > Date.now() - 2500;
  }

  messages = [...messages]
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

  useEffect(() => {
    if (messages.length > previousMessageLength.current) {
      //  Not on mount.
      historyListScrollTop[id] = undefined;
    }
  }, [messages.length]);

  useEffect(() => {
    //  Periodically update the message timestamps, typing indicator, etc.
    const tickId = setInterval(() => setNonce(Date.now()), 3e3);

    return () => { clearInterval(tickId); }
  }, []);

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <div
        {...props}
        className={styles.historyList}
        data-chat-history-list
        ref={element => {
          listRef.current = element;

          if (element && autoScrollToEnd) {
            element.scrollTop = historyListScrollTop[id] ?? 99999;
          }
        }}
        onScroll={event => {
          historyListScrollTop[id] = event.currentTarget.scrollTop;
        }}>
        {
          isBusy ? (
            <div style={{ width: '100%', height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TaskIndicator type="tiny" />
            </div>
          ) : null
        }
        {
          ingress ? (
            <Placeholder size="small" style={{ whiteSpace: 'pre-line', padding: '12px 0' }}>
              {ingress}
            </Placeholder>
          ) : null
        }
        {
          messages.length ? (
            messages.map(message => (
              <HistoryListItem
                key={message.id}
                message={message} />
            ))
          ) : ingress || isBusy ? null : (
            <Placeholder size="small">
              {text`Inga skickade meddelanden än`}
            </Placeholder>
          )
        }
        {
          //  Skicka isTyping=null för att påtvinga detta element, då det annars inte ska visas i alla implementationer.
          typeof isTyping === 'boolean' || isTyping === null ? (
            //  Fick inte TransitionedContainer att fungera.
            <div className={cls([styles.typing, isTyping ? styles.visible : null])}>
              <div></div>
              <div></div>
              <div></div>
            </div>
          ) : null
        }
      </div>
    </div>
  )
}

type HistoryListItemProps = {
  message: GenericChatMessage;
}

const HistoryListItem: React.FC<HistoryListItemProps> = ({ message }) => {
  const { users } = useStore(globalUserStore);

  const senderUserName = message.userId ? users.getPreferredName(message.userId) : null;

  let tooltip = text`${formatDate(message.createdAt, { time: true })}`;

  if (senderUserName && !message.isThem) {
    tooltip = text`${formatDate(message.createdAt, { time: true })} av ${senderUserName}`;
  }

  if (message.tooltip !== false) {
    tooltip = message.tooltip || text`Skickat ${tooltip}`;
  }

  return (
    <div {...message.props}
      className={cls([
        message.props?.className,
        styles.historyListItem,
        message.isThem ? styles.inbound : null,
        !message.status ? styles.sms : null
      ])}>
      {
        message.status ? (
          <div className={styles.status}>
            {
              message.error ? (
                <TooltipTrigger tooltip={text`Meddelandet kunde inte skickas. Prova igen.`} delay={false}>
                  <FontAwesomeIcon className={styles.icon} name="exclamation-triangle" />
                </TooltipTrigger>

              ) : message.status.busy ? (
                <TaskIndicator type="tiny" className={styles.busy} />

              ) : message.status.seen && !message.isThem ? (
                <TooltipTrigger tooltip={text`Sett`} delay={false}>
                  <FontAwesomeIcon className={styles.icon} name="check" />
                </TooltipTrigger>

              ) : message.status.sent && !message.isThem ? (
                <TooltipTrigger tooltip={text`Skickat`} delay={false}>
                  <FontAwesomeIcon className={styles.icon} name="check" style={{ opacity: 0.5 }} />
                </TooltipTrigger>

              ) : message.status.notSent ? (
                //  Not acknowledged after 10 seconds...
                <TooltipTrigger tooltip={text`Meddelandet kunde inte levereras. Prova igen.`} delay={false}>
                  <FontAwesomeIcon className={styles.icon} name="question" />
                </TooltipTrigger>
              ) : null
            }
          </div>
        ) : null
      }

      <TooltipTrigger
        className={styles.text}
        delay={false}
        tooltip={tooltip}>
        <div className={styles.time}>
          {
            message.dateText || formatDate(message.createdAt, {
              hideTodaysDate: true, time: true
            })
          }
        </div>
        <div>
          {message.isConfetti && !message.text ? `🎉 ${text`Skickat konfetti`}` : message.text}
        </div>
      </TooltipTrigger>
    </div>
  );
}